import {
  disponibilidadeBucketLabel,
  axisValueLabel,
  TEMPERATURA_CONFIG,
  type Temperatura,
} from "@/utils/disponibilidadeUtils";

export type FilterValues = Record<string, string | string[] | undefined>;

/** Opção "Palavra exata" da busca: vai em p_filters, então URL e buscas salvas já a guardam. Não é um filtro da lista. */
export const SEARCH_EXACT_FILTER = "search_exact";

/** Valores aceitos em `external_links` pela RPC search_freelancers (OR entre eles). */
export const EXTERNAL_LINK_LABELS: Record<string, string> = {
  linkedin: "Possui LinkedIn",
  linkedin_valido: "LinkedIn válido",
  linkedin_invalido: "LinkedIn inválido ou não encontrado",
  linkedin_vazio: "Sem LinkedIn",
  portfolio: "Possui portfólio",
  portfolio_invalido: "Portfólio inválido",
};

export const PORTFOLIO_STATUS_OPTIONS = [
  { value: "portfolio", label: EXTERNAL_LINK_LABELS.portfolio },
  { value: "portfolio_invalido", label: EXTERNAL_LINK_LABELS.portfolio_invalido },
];

export const LINKEDIN_STATUS_OPTIONS = [
  { value: "linkedin_valido", label: EXTERNAL_LINK_LABELS.linkedin_valido },
  { value: "linkedin_invalido", label: EXTERNAL_LINK_LABELS.linkedin_invalido },
  { value: "linkedin_vazio", label: EXTERNAL_LINK_LABELS.linkedin_vazio },
];

/** Valores de `open_to_work` na RPC search_freelancers (sql/294): "desconhecido" = nenhuma busca leu o selo. */
export const OPEN_TO_WORK_LABELS: Record<string, string> = {
  sim: "Sim",
  nao: "Não",
  desconhecido: "Sem leitura",
};

export const OPEN_TO_WORK_OPTIONS = Object.entries(OPEN_TO_WORK_LABELS).map(([value, label]) => ({ value, label }));

/** Corte operacional de pretensão (COR-388): "até R$ 2.500,00" fica de fora. */
export const SALARY_BAND_CUTOFF_REAIS = 2500;

interface ChipLabel {
  key: string;
  label: string;
  summary: string;
}

const FRIENDLY_LABEL: Record<string, string> = {
  areas: "Áreas",
  pretensoes_salariais: "Pretensão",
  tipo_atuacao: "Tipo de atuação",
  interesse_vaga_fixa: "Vaga fixa",
  interesse_em_mudar_emprego: "Quer mudar de emprego",
  interesse_presencial: "Presencial",
  cidade: "Cidade",
  city: "Cidade",
  state: "Estado",
  country: "País",
  idiomas: "Idiomas",
  tags: "Tags",
  auto_tags: "Tags IA",
  quality_seal: "Selo",
  is_active: "Status",
  external_links: "Links",
  falar_com_frila: "Falar com Frila",
  open_to_work: "Open to Work",
  atributos_selecionados: "Atributos",
  avaliado_perfil: "Avaliado (perfil)",
  avaliado_projeto: "Avaliado (projeto)",
  min_media_projeto: "Min. projeto",
  min_media_perfil: "Min. perfil",
  min_maior_nota: "Min. maior nota",
  disponibilidade: "Disponibilidade",
  disp_emprego: "Situação de emprego",
  disp_interesse: "Interesse em freela",
  disp_relacao: "Relação com a gente",
  disp_temperatura: "Temperatura",
};

const AXIS_DIMENSION_BY_KEY: Record<string, string> = {
  disp_emprego: "situacao_emprego",
  disp_interesse: "interesse_freela",
  disp_relacao: "relacao_conosco",
};

/** Booleano string ("true"/"false") → texto humano. */
function booleanLabel(value: string, trueLabel = "Sim", falseLabel = "Não"): string {
  if (value === "true") return trueLabel;
  if (value === "false") return falseLabel;
  return value;
}

/** Resumo curto p/ chip. Arrays viram "N selecionados" se >2 itens. */
function summarize(key: string, value: string | string[]): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    // Disponibilidade é um bucket com nome próprio (ex.: "Vale abordar"), não a lista crua de valores.
    if (key === "disponibilidade") return disponibilidadeBucketLabel(value);
    if (AXIS_DIMENSION_BY_KEY[key]) {
      return value.map((item) => axisValueLabel(AXIS_DIMENSION_BY_KEY[key], item)).join(" + ");
    }
    if (key === "disp_temperatura") {
      return value.map((item) => TEMPERATURA_CONFIG[item as Temperatura]?.text ?? item).join(" + ");
    }
    if (key === "external_links") {
      return value.map((item) => EXTERNAL_LINK_LABELS[item] ?? item).join(" + ");
    }
    if (value.length <= 2) return value.join(", ");
    return `${value.length} selecionados`;
  }
  if (key === "is_active") return booleanLabel(value, "Ativos", "Inativos");
  if (key === "quality_seal") return booleanLabel(value, "Verificado", "Não verificado");
  if (key === "interesse_vaga_fixa") return booleanLabel(value);
  if (key === "interesse_em_mudar_emprego") return booleanLabel(value);
  if (key === "falar_com_frila") return booleanLabel(value, "Marcados", "Não marcados");
  if (key === "open_to_work") return OPEN_TO_WORK_LABELS[value] ?? value;
  return value;
}

/**
 * Lista os filtros ativos para renderizar como chips. Ignora `is_active="true"`
 * (default implícito) para não poluir, mas mantém `is_active="false"`.
 */
export function getActiveFilterChips(filters: FilterValues): ChipLabel[] {
  const chips: ChipLabel[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (key === SEARCH_EXACT_FILTER) continue;
    if (value === undefined || value === "" || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (key === "is_active" && value === "true") continue;
    chips.push({
      key,
      label: FRIENDLY_LABEL[key] ?? key,
      summary: summarize(key, value),
    });
  }
  return chips;
}

/** Total de filtros ativos (mesmo critério usado em V1 hasActiveFilters). */
export function countActiveFilters(filters: FilterValues): number {
  return getActiveFilterChips(filters).length;
}

/** Extrai o primeiro número de uma pretensão em texto livre ("R$ 500/dia" -> 500). */
function extractSalaryValue(salary: string): number {
  const numericValue = salary.replace(/[^0-9,.]/g, "");
  const matches = numericValue.match(/\d+([,.]\d+)?/);
  if (matches && matches[0]) {
    return parseFloat(matches[0].replace(",", "."));
  }
  return Number.MAX_SAFE_INTEGER;
}

/** Ordena pretensões em texto livre: "a combinar" primeiro, depois valor crescente. */
export function sortSalaryValues(salaries: string[]): string[] {
  return [...salaries].sort((a, b) => {
    if (a.toLowerCase().includes("combinar")) return -1;
    if (b.toLowerCase().includes("combinar")) return 1;
    const aOpenEnded = a.toLowerCase().includes("a partir");
    const bOpenEnded = b.toLowerCase().includes("a partir");
    if (aOpenEnded && !bOpenEnded) return -1;
    if (!aOpenEnded && bOpenEnded) return 1;
    return extractSalaryValue(a) - extractSalaryValue(b);
  });
}

/** Maior valor em reais citado numa faixa ("entre R$ 2.600,00 e R$ 5.000,00" -> 5000). Null sem número. */
export function parseSalaryBandMaxReais(band: string): number | null {
  const amounts = band.match(/\d{1,3}(?:\.\d{3})*(?:,\d+)?|\d+(?:,\d+)?/g);
  if (!amounts) return null;
  const values = amounts.map((amount) => parseFloat(amount.replace(/\./g, "").replace(",", ".")));
  return Math.max(...values);
}

/** Faixa cujo teto passa do corte; "até R$ 2.500,00" tem teto 2500 e fica de fora. */
export function isSalaryBandAbove(band: string, cutoffReais: number): boolean {
  const max = parseSalaryBandMaxReais(band);
  return max !== null && max > cutoffReais;
}
